/**
 * @fileoverview User-visible chat error lines with stable six-digit codes (docs/ERROR_CODES.md).
 */

import { ERR_AUTH_401, ERR_CHAT_500 } from './error-codes';
import { formatErrorMessageWithId } from './error-format-helpers';
import { getErrorIdEntry } from './error-ids';
import { isKorvoError, KorvoError } from './korvo-error';

/** KorvoChat client catch: unknown errors normalized before display. */
export const CHAT_CLIENT_UNKNOWN_THROW_ID = 10;

/** KorvoChat: model returned no assistant content. */
export const CHAT_EMPTY_RESPONSE_THROW_ID = 11;

/** ChatInstance: sendMessage failure. */
export const CHAT_INSTANCE_SEND_THROW_ID = 12;

/** ChatInstance: thread creation failure. */
export const CHAT_INSTANCE_THREAD_CREATE_THROW_ID = 13;

/** ChatInstance: stream error chunk. */
export const CHAT_INSTANCE_STREAM_THROW_ID = 14;

/** ChatInstance: stream handler failure. */
export const CHAT_INSTANCE_STREAM_HANDLER_THROW_ID = 15;

/** ChatInstance: init failure. */
export const CHAT_INSTANCE_INIT_THROW_ID = 16;

export function ensureKorvoErrorForChat(
  error: unknown,
  options?: { baseCode?: string; uniqueThrowId?: number },
): KorvoError {
  if (isKorvoError(error)) {
    return error;
  }
  const baseCode = options?.baseCode ?? ERR_CHAT_500;
  const uniqueThrowId = options?.uniqueThrowId ?? CHAT_CLIENT_UNKNOWN_THROW_ID;
  const message = error instanceof Error ? error.message : String(error);
  return new KorvoError(baseCode, uniqueThrowId, { message, status: 500 });
}

export function describeChatErrorMessage(korvo: KorvoError): string {
  const entry = getErrorIdEntry(korvo.id);
  const raw = korvo.message.trim();

  if (korvo.baseCode === ERR_AUTH_401) {
    return entry?.userMessage ?? 'Session expired or invalid. Please sign in again.';
  }
  if (raw.includes('returned no content') || raw.includes('No response was received')) {
    return 'The model returned no content. Check API key, rate limits, or try a different model.';
  }
  if (raw.includes('Failed to create thread')) {
    return 'Failed to create thread';
  }
  if (raw.includes('timeout') || raw.includes('Request timeout')) {
    return 'Request timeout. Please check your connection';
  }
  if (raw.includes('NetworkError') || (raw.toLowerCase().includes('network') && raw.includes('fetch'))) {
    return 'Network error. Please check your connection';
  }

  let cleaned = raw
    .replace(/^Error:\s*/i, '')
    .replace(/\s*\(ERR_[A-Z0-9_]+\)\s*$/i, '')
    .replace(/\.\s*Please try again\.?\s*$/i, '')
    .trim();

  if (cleaned !== '' && cleaned !== korvo.baseCode) {
    return cleaned;
  }

  return entry?.userMessage ?? 'An error occurred';
}

export function formatChatClientErrorMessage(
  error: unknown,
  options: { isAdmin: boolean },
): string {
  const korvo = ensureKorvoErrorForChat(error);
  return formatErrorMessageWithId(describeChatErrorMessage(korvo), korvo, options);
}

export function formatChatErrorForDisplay(
  message: string,
  korvoId: string | undefined,
  options: { isAdmin: boolean },
): string {
  if (korvoId !== undefined && korvoId.trim() !== '') {
    return formatErrorMessageWithId(message, { id: korvoId }, options);
  }
  return formatChatClientErrorMessage(new Error(message), options);
}
